/**
 * Tracking wrapper — point d'entrée unique pour émettre un event vers Better Stack.
 *
 * V0 : ingestion HTTP vers la source `frontend-events` (BS team Homeys, eu-fsn-3).
 * L'enveloppe canonique est remplie depuis `auth.store` (user_id, actor_type,
 * company_id, company_type, role, subscriptions, session_id).
 *
 * **Garanties de résilience** (priorité absolue : ne JAMAIS impacter l'app) :
 * - Wrap try/catch externe : aucune exception ne propage hors de `track()`
 * - Fire-and-forget : appel synchrone, pas d'`await` requis
 * - Timeout 3s sur le POST : si BS hang, on abandonne sans bloquer
 * - BS down / 500 / token invalide → silencieux, l'app tourne
 *
 * Usage type-safe (l'event name discriminate le payload) :
 *
 *   import { track } from "@core/tracking/track"
 *   track({ event: "Page Viewed", payload: { route: "/" } })
 *
 * Doc projet : `top/docs/projects/tracking-product-plan-v1.md`.
 */

import { watch } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { mentionStaffDuJeton } from "@core/stores/auth.staff"
import type { OrigineSession, TrackedEvent } from "./events"
import type {
    ActorSource,
    ActorType,
    CompanyType,
    Role,
    TrackingEnvelope,
} from "./envelope"
import { deduireEnv } from "./env"
import { deriveModuleFromRoute } from "./router-module-map"

const BS_URL = import.meta.env.VITE_TRACKING_BS_URL as string | undefined
const BS_TOKEN = import.meta.env.VITE_TRACKING_BS_TOKEN as string | undefined

/** Timeout d'ingestion BS (ms). Au-delà → abandon silencieux. */
const INGEST_TIMEOUT_MS = 3000

/** Fallback compagnie type si non encore chargé côté store. */
const DEFAULT_COMPANY_TYPE: CompanyType = "entreprise"
/** Fallback role si non encore chargé côté store. */
const DEFAULT_ROLE: Role = "employe"

/**
 * Rôles que le plan de suivi (`plan.yaml`, enum `role`) accepte : les mêmes que le
 * store d'auth, `client` (UC2) compris depuis la v3 du plan (DEV-2470). Un rôle
 * connu part tel quel, jamais projeté sur un autre. Le repli `employe` ne sert
 * qu'à un rôle `null` : pas encore chargé, ou absent de la réponse d'api-metier
 * (`userCompanyType` vide ou hors liste), auquel cas la session est comptée
 * `employe` sans trace.
 */
export const ROLES_DU_PLAN: readonly Role[] = [
    "owner",
    "administrateur",
    "employe",
    "employe_read_only",
    "client",
]

export function roleDuPlan(role: string | null | undefined): Role {
    return role && (ROLES_DU_PLAN as readonly string[]).includes(role)
        ? (role as Role)
        : DEFAULT_ROLE
}

/**
 * LA FILE D'ATTENTE (DEV-1508 lot 3).
 *
 * Un événement émis avant que l'utilisateur soit connu était PERDU, sans trace
 * ni journal. C'est le sort de tout écran qui parle au montage : la page se
 * monte pendant que `GET /user/me` est encore en vol, et si cet appel échoue une
 * fois, la session entière devient muette pour toujours.
 *
 * On garde donc ces événements de côté, avec leur horodatage d'origine, et on
 * les verse dès que l'utilisateur est connu. Bornée : au-delà, on jette les plus
 * ANCIENS, pour qu'une session jamais authentifiée ne fasse pas grossir la
 * mémoire indéfiniment.
 */
const TAILLE_FILE = 50

/**
 * Où se trouvait l'utilisateur quand l'événement a été émis (DEV-2457). Saisi à
 * l'ÉMISSION, comme l'horodatage : un événement différé garde sa route.
 */
interface ContexteDeNavigation {
    module?: string
    route_name?: string
    app_host?: string
}

interface EvenementDiffere {
    event: TrackedEvent
    timestamp: string
    contexte: ContexteDeNavigation
}

let file: EvenementDiffere[] = []
let veilleInstallee = false

/** Dernière route confirmée par le router (posée par le plugin de suivi). */
let routeCourante: { module?: string; route_name?: string } = {}

/**
 * Note la route que le router vient de confirmer. Appelé par le plugin de
 * suivi à chaque navigation, AVANT d'émettre `Page Viewed`. On garde le nom de
 * la route et le module, jamais le chemin : il porte des identifiants.
 */
export function noterLaNavigation(route: {
    path: string
    name?: unknown
}): void {
    try {
        routeCourante = {
            module: deriveModuleFromRoute(route.path),
            route_name: typeof route.name === "string" ? route.name : undefined,
        }
    } catch {
        routeCourante = {}
    }
}

function contexteDeNavigation(): ContexteDeNavigation {
    let appHost: string | undefined
    try {
        appHost =
            typeof window !== "undefined"
                ? window.location?.host || undefined
                : undefined
    } catch {
        appHost = undefined
    }
    return { ...routeCourante, app_host: appHost }
}

/**
 * Vrai dès qu'un jeton lu pendant ce chargement de page portait la mention
 * signée `staff` (DEV-2457). Certains parcours remplacent le jeton en cours de
 * route (signature de consentement, validation de compte, invitations PDL et
 * PCE) par un jeton émis SANS la mention, alors que la personne devant l'écran
 * est toujours l'équipe. Mémoire de page seulement : un rechargement l'oublie.
 */
let equipeSigneeDansCeChargement = false

/**
 * L'acteur de la session (DEV-2457). La mention que le serveur SIGNE dans le
 * jeton décide : `homeys_staff` quand elle vaut vrai, `client` sinon. Le
 * drapeau `adminHomeys` du stockage du navigateur, que n'importe qui peut
 * écrire, ne sert plus que de repli quand aucun jeton n'est lisible.
 *
 * Un cas fait exception, `heritage` : le jeton courant est lisible sans la
 * mention, mais un jeton lu plus tôt dans le même chargement la portait et le
 * drapeau est resté vrai. C'est un parcours qui a remplacé le jeton de
 * l'équipe ; la session reste `homeys_staff`. Le drapeau seul ne suffit jamais
 * à faire l'équipe : sans jeton signé vu dans ce chargement, c'est `client`.
 * Les valeurs d'`actor_type` ne changent pas : les outils de lecture excluent
 * d'office une valeur qu'ils ne connaissent pas.
 */
export function acteurDeLaSession(
    jeton: string | null | undefined,
    adminHomeys: boolean | null | undefined
): { actor_type: ActorType; actor_source: ActorSource } {
    // Une déconnexion remet le drapeau à faux : la mémoire part avec lui.
    if (!adminHomeys) equipeSigneeDansCeChargement = false
    const mention = mentionStaffDuJeton(jeton)
    if (mention === null) {
        return {
            actor_type: adminHomeys ? "homeys_staff" : "client",
            actor_source: "repli",
        }
    }
    if (mention) {
        equipeSigneeDansCeChargement = true
        return { actor_type: "homeys_staff", actor_source: "jeton" }
    }
    if (adminHomeys && equipeSigneeDansCeChargement) {
        return { actor_type: "homeys_staff", actor_source: "heritage" }
    }
    return { actor_type: "client", actor_source: "jeton" }
}

/**
 * Lit le jeton à l'ÉMISSION d'un événement, pour que la mention de l'équipe
 * soit notée avant qu'un parcours ne remplace le jeton, même si l'événement,
 * lui, part plus tard. Ne lève jamais.
 */
function noterLeJeton(authStore: ReturnType<typeof useAuthStore>): void {
    try {
        acteurDeLaSession(authStore.token, authStore.adminHomeys)
    } catch {
        // Rien à noter.
    }
}

/**
 * Ouvre une session de suivi (DEV-2457) : nouvel identifiant de session, puis
 * `Session Started` avec son origine. `reprise` = jeton restauré au chargement
 * de l'application, `connexion` = formulaire. Ne lève jamais.
 */
export function ouvrirUneSessionDeSuivi(origine: OrigineSession): void {
    try {
        useAuthStore().generateNewSession()
    } catch {
        // Pinia absente : pas de session à ouvrir.
        return
    }
    track({ event: "Session Started", payload: { origine } })
}

interface StripeSubscriptionLike {
    metadata?: { tag?: string }
    status?: string
}

function extractSubscriptionTags(
    subscriptions: readonly StripeSubscriptionLike[] | undefined
): string[] {
    if (!subscriptions || subscriptions.length === 0) return []
    const tags: string[] = []
    for (const sub of subscriptions) {
        if (sub.status === "canceled") continue
        const raw = sub.metadata?.tag
        if (!raw) continue
        for (const t of raw.split(",")) {
            const trimmed = t.replace(/\s/g, "")
            if (trimmed) tags.push(trimmed)
        }
    }
    return tags
}

/**
 * Émet un event tracking. Le `event` discriminate le `payload` au type-check.
 *
 * Skip silencieusement si :
 * - VITE_TRACKING_BS_URL/TOKEN manquants (build sans tracking)
 * - Pinia pas encore prête (très tôt au boot)
 *
 * N'est PAS jeté, mais mis en file et versé plus tard, l'événement émis avant
 * que l'utilisateur soit connu (DEV-1508 lot 3). C'est le cas de tout écran qui
 * parle au montage : la page se monte pendant que `GET /user/me` est en vol.
 *
 * Garantit : aucune exception, aucun blocage, aucun impact UX si BS down.
 *
 * @param event L'event typé (cf. ./events.ts)
 */
export function track<E extends TrackedEvent>(event: E): void {
    try {
        if (!BS_URL || !BS_TOKEN) {
            if (import.meta.env.DEV) {
                console.debug(
                    "[track] Skipped (BS not configured): VITE_TRACKING_BS_URL/TOKEN missing"
                )
            }
            return
        }

        let authStore: ReturnType<typeof useAuthStore>
        try {
            authStore = useAuthStore()
        } catch {
            // Pinia pas encore initialisée (très rare, juste au boot)
            return
        }

        const contexte = contexteDeNavigation()
        noterLeJeton(authStore)

        if (!authStore.isLogged || !authStore.userId) {
            // L'utilisateur n'est pas encore connu : on garde l'événement au lieu
            // de le perdre, et on installe la veille qui le versera.
            differer(event, contexte)
            installerLaVeille(authStore)
            return
        }

        viderLaFile(authStore)
        envoyer(event, authStore, new Date().toISOString(), contexte)
    } catch {
        // Garde-fou final : si quoi que ce soit ci-dessus throw (improbable),
        // on absorbe sans propager. Le tracking ne doit JAMAIS planter l'app.
    }
}

/** Met un événement de côté, le plus ancien cédant sa place si la file est pleine. */
function differer(event: TrackedEvent, contexte: ContexteDeNavigation): void {
    file.push({ event, timestamp: new Date().toISOString(), contexte })
    if (file.length > TAILLE_FILE) file = file.slice(file.length - TAILLE_FILE)
}

/**
 * Installe UNE fois la veille sur l'authentification : dès que l'utilisateur est
 * connu, la file part. Sans elle, un écran qui parle au montage attendrait le
 * prochain événement pour être versé, et il pourrait ne jamais venir.
 */
function installerLaVeille(authStore: ReturnType<typeof useAuthStore>): void {
    if (veilleInstallee) return
    veilleInstallee = true
    try {
        watch(
            () => authStore.isLogged && Boolean(authStore.userId),
            (pret) => {
                if (pret) viderLaFile(authStore)
            }
        )
    } catch {
        // Sans veille (hors contexte réactif), la file part au prochain événement.
        veilleInstallee = false
    }
}

/** Verse la file dans l'ordre d'émission. */
function viderLaFile(authStore: ReturnType<typeof useAuthStore>): void {
    if (file.length === 0) return
    const enAttente = file
    file = []
    for (const differe of enAttente) {
        envoyer(differe.event, authStore, differe.timestamp, differe.contexte)
    }
}

/** Remet la file et la veille à zéro. Réservé aux tests. */
export function reinitialiserLaFile(): void {
    file = []
    veilleInstallee = false
    routeCourante = {}
    equipeSigneeDansCeChargement = false
}

/**
 * Construit l'enveloppe et lance le POST. L'horodatage est celui de l'ÉMISSION,
 * pas celui de l'envoi : un événement différé garde sa place dans le temps.
 */
function envoyer(
    event: TrackedEvent,
    authStore: ReturnType<typeof useAuthStore>,
    timestamp: string,
    contexte: ContexteDeNavigation
): void {
    try {
        const acteur = acteurDeLaSession(authStore.token, authStore.adminHomeys)

        const envelope: TrackingEnvelope = {
            timestamp,
            event: event.event,
            event_version: 1,
            env: deduireEnv(
                typeof window !== "undefined"
                    ? window.location?.hostname
                    : null,
                Boolean(import.meta.env?.DEV)
            ),

            user_id: authStore.userId,
            actor_type: acteur.actor_type,
            actor_source: acteur.actor_source,
            session_id: authStore.sessionId,

            company_id: authStore.companyId,
            company_type: authStore.companyType ?? DEFAULT_COMPANY_TYPE,
            subscription_plan:
                (authStore.subscription as { plan?: string } | undefined)
                    ?.plan ?? "",
            role: roleDuPlan(authStore.role),
            subscriptions: extractSubscriptionTags(
                authStore.companyStripeSubscriptions as
                    | readonly StripeSubscriptionLike[]
                    | undefined
            ),

            module: contexte.module,
            route_name: contexte.route_name,
            app_host: contexte.app_host,

            payload: event.payload as Record<string, unknown>,
        }

        let body: string
        try {
            body = JSON.stringify(envelope)
        } catch {
            // Payload contient un cycle ou une valeur non-serializable.
            return
        }

        if (import.meta.env.DEV) {
            console.debug("[track]", envelope)
        }

        // Fire-and-forget POST avec timeout. Toute erreur (réseau, 5xx, abort) est silencieuse.
        const controller =
            typeof AbortController !== "undefined"
                ? new AbortController()
                : null
        const timeoutId = controller
            ? setTimeout(() => controller.abort(), INGEST_TIMEOUT_MS)
            : null

        fetch(BS_URL as string, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${BS_TOKEN}`,
            },
            body,
            signal: controller?.signal,
            // pas de keepalive : provoque des reports 503 mensongers en DevTools
            // alors que BS reçoit bien (vu en prod 2026-05-07). Trade-off accepté V0 :
            // les events émis pendant un unload de page peuvent être perdus.
        })
            .catch(() => {
                // Ingestion silencieuse : on ne propage rien.
            })
            .finally(() => {
                if (timeoutId !== null) clearTimeout(timeoutId)
            })
    } catch {
        // Un envoi qui casse ne casse rien d'autre : le suivi ne doit JAMAIS
        // planter l'écran.
    }
}
